import { Alert, Badge, Card, ListGroup } from "react-bootstrap";
import type { IMappingValidationIssue, IMappingValidationResult } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { coerceMappingValidationResult } from "./mappingValidation";
import {
    formatMappingValidationWarningMessage,
    getDisplayedMappingValidationWarningCount,
    getDisplayedMappingValidationWarnings,
    isExportReadyForDisplay,
} from "./mappingValidationDisplay";

export interface TemplateValidationSummaryProps {
    validation: IMappingValidationResult | null | undefined;
}

export function formatValidationIssueContext(issue: IMappingValidationIssue): string {
    const parts: string[] = [];
    if (issue.section) parts.push(issue.section);
    if (issue.fieldKey) parts.push(issue.fieldKey);
    if (issue.table) parts.push(`table:${issue.table}`);
    if (issue.column) parts.push(`col:${issue.column}`);
    if (issue.sheet) parts.push(`sheet:${issue.sheet}`);
    if (issue.address) parts.push(issue.address);
    return parts.join(" · ");
}

function IssueList({
    issues,
    variant,
    formatMessage,
}: {
    issues: IMappingValidationIssue[];
    variant: "danger" | "warning";
    formatMessage?: (issue: IMappingValidationIssue) => string;
}) {
    if (issues.length === 0) {
        return <Alert variant={variant === "danger" ? "success" : "secondary"} className="py-2 small mb-0">None</Alert>;
    }

    return (
        <ListGroup variant="flush">
            {issues.map((issue, index) => (
                <ListGroup.Item key={`${issue.code}-${index}`} className="px-0">
                    <div>
                        <Badge bg={variant} className="me-2">
                            {issue.code}
                        </Badge>
                        {formatMessage ? formatMessage(issue) : issue.message}
                    </div>
                    <div className="text-muted small">{formatValidationIssueContext(issue)}</div>
                </ListGroup.Item>
            ))}
        </ListGroup>
    );
}

export function TemplateValidationSummary({ validation }: TemplateValidationSummaryProps) {
    const mappingValidation = coerceMappingValidationResult(validation);

    if (!mappingValidation) {
        return (
            <Card className="mb-4">
                <Card.Header>Mapping Validation</Card.Header>
                <Card.Body>
                    <Alert variant="info" className="mb-0">
                        Save or validate the mapping to see validation results.
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    const errors = mappingValidation.errors;
    const displayedWarnings = getDisplayedMappingValidationWarnings(mappingValidation);
    const displayedWarningCount = getDisplayedMappingValidationWarningCount(mappingValidation);
    const exportReadyForDisplay = isExportReadyForDisplay(mappingValidation);

    return (
        <Card className="mb-4">
            <Card.Header>Mapping Validation</Card.Header>
            <Card.Body>
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <Badge bg={mappingValidation.isValid ? "success" : "danger"}>
                        {mappingValidation.isValid ? "Valid" : "Has blocking errors"}
                    </Badge>
                    <Badge bg={exportReadyForDisplay ? "success" : "warning"}>
                        {exportReadyForDisplay ? "Export ready" : "Review warnings before export"}
                    </Badge>
                    <span className="text-muted small align-self-center">
                        Checked {new Date(mappingValidation.checkedAtUtc).toLocaleString()}
                    </span>
                </div>

                <Alert variant="secondary" className="py-2 small">
                    <strong>Errors</strong> block export readiness. <strong>Warnings</strong> require review but do not block saving or editing.
                </Alert>

                <h3 className="h6 text-danger">Errors ({errors.length})</h3>
                <IssueList issues={errors} variant="danger" />

                <h3 className="h6 text-warning mt-4">Warnings ({displayedWarningCount})</h3>
                <IssueList
                    issues={displayedWarnings}
                    variant="warning"
                    formatMessage={formatMappingValidationWarningMessage}
                />
            </Card.Body>
        </Card>
    );
}
